import { pb } from './pocketbase';
import { createAttendance, getAttendancesBySessionId, deleteAttendance } from './attendances.service';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export const getSessionsFormation = async ({ page = 1, perPage = 10, filter = null, sort = '-created' } = {}) => {
    try {
        const records = await pb.collection('sessions').getList(page, perPage, {
            sort: sort,
            filter: filter,
            expand: 'teacher_id,session_id'
        });
        
        return {
            items: records.items,
            totalPages: records.totalPages,
            totalItems: records.totalItems,
            page: records.page,
            perPage: records.perPage
        };
    } catch (error) {
        if (error.status === 404) {
            return {
                items: [],
                totalPages: 1,
                totalItems: 0,
                page: 1,
                perPage: perPage
            };
        }
        console.error(`Erreur lors de la récupération des sessions de formation:`, error);
        throw error;
    }
};

export const getSessionFormationById = async (sessionId) => {
    try {
        const session = await pb.collection('sessions').getOne(sessionId, {
            expand: 'teacher_id,session_id'
        });
        return session;
    } catch (error) {
        console.error(`Erreur lors de la récupération de la session ${sessionId}:`, error);
        throw error;
    }
};

// Fonction pour générer un code unique à 4 chiffres
const generateSessionCode = () => {
    // Génère un nombre aléatoire entre 1000 et 9999 (4 chiffres)
    return Math.floor(1000 + Math.random() * 9000).toString();
};

export const createSessionFormation = async (sessionData) => {
    try {
        if (pb.authStore.isValid && pb.authStore.model) {
            const sessionWithPartnerId = {
                ...sessionData,
                partner_id: pb.authStore.record.partner_id,
                code: generateSessionCode()
            };

            // 1. Création de la session
            const createdSession = await pb.collection('sessions').create(sessionWithPartnerId);

            // 2. Création automatique des attendances basées sur le session_group
            if (createdSession.session_id) {
                console.log('Session group à traiter:', createdSession.session_id);
                
                // Récupérer les membres du groupe de session directement
                const groupMembers = await pb.collection('session_group_members').getFullList({
                    filter: `session_group_id = "${createdSession.session_id}"`,
                    expand: 'student_id'
                });
                
                console.log('Membres du groupe trouvés:', groupMembers.length);
                
                for (const member of groupMembers) {
                    try {
                        const studentId = member.student_id;
                        if (!studentId) {
                            console.error(`Aucun student_id trouvé pour le membre ${member.id}`);
                            continue;
                        }
                        
                        const attendanceData = {
                            session_id: createdSession.id,
                            participant_id: studentId, // Utiliser directement le student_id
                            partner_id: createdSession.partner_id,
                            status: 'Absent',
                            signature_token: generateSessionCode() // Token unique pour la signature
                        };
                        
                        console.log('Création attendance pour étudiant:', studentId, attendanceData);
                        const attendance = await createAttendance(attendanceData);
                        console.log('Attendance créée avec succès:', attendance.id);
                    } catch (attendanceError) {
                        console.error(`Erreur lors de la création de l'attendance pour le membre ${member.id}:`, attendanceError);
                        // Continue avec les autres participants même si un échoue
                    }
                }
            }

            return createdSession;
        } else {
            console.error('Aucun utilisateur authentifié. Impossible de créer la session sans partner_id.');
            return null;
        }
    } catch (error) {
        console.error('Erreur lors de la création de la session de formation:', error);
        throw error;
    }
};

export const updateSessionFormation = async (sessionId, sessionData) => {
    try {
        const updatedSession = await pb.collection('sessions').update(sessionId, sessionData);
        return updatedSession;
    } catch (error) {
        console.error(`Erreur lors de la mise à jour de la session ${sessionId}:`, error);
        throw error;
    }
};

export const deleteSessionFormation = async (sessionId) => {
    try {
        // 1. Récupérer et supprimer toutes les attendances liées à cette session
        console.log('Suppression des attendances pour la session:', sessionId);
        const attendances = await getAttendancesBySessionId(sessionId);
        
        if (attendances && attendances.length > 0) {
            console.log(`${attendances.length} attendance(s) à supprimer`);
            
            for (const attendance of attendances) {
                try {
                    await deleteAttendance(attendance.id);
                    console.log('Attendance supprimée:', attendance.id);
                } catch (attendanceError) {
                    console.error(`Erreur lors de la suppression de l'attendance ${attendance.id}:`, attendanceError);
                    // Continue avec les autres attendances même si une échoue
                }
            }
        } else {
            console.log('Aucune attendance à supprimer pour cette session');
        }
        
        // 2. Supprimer la session
        console.log('Suppression de la session:', sessionId);
        await pb.collection('sessions').delete(sessionId);
        console.log('Session supprimée avec succès');
        
        return true;
    } catch (error) {
        console.error(`Erreur lors de la suppression de la session ${sessionId}:`, error);
        throw error;
    }
};

export const deleteMultipleSessionsFormation = async (sessionIds) => {
    try {
        // Supprimer chaque session une par une pour gérer les attendances
        for (const sessionId of sessionIds) {
            try {
                await deleteSessionFormation(sessionId);
                console.log(`Session ${sessionId} et ses attendances supprimées avec succès`);
            } catch (error) {
                console.error(`Erreur lors de la suppression de la session ${sessionId}:`, error);
                // Continue avec les autres sessions même si une échoue
            }
        }
        return true;
    } catch (error) {
        console.error('Erreur lors de la suppression en masse des sessions:', error);
        throw error;
    }
};

export const duplicateSessionFormation = async (sessionId) => {
    try {
        // Récupérer la session originale
        const originalSession = await pb.collection('sessions').getOne(sessionId);
        
        // Créer une copie sans l'ID et avec un titre modifié
        // Note: description (commentaire) n'est pas copié intentionnellement
        const duplicatedData = {
            title: `${originalSession.title} (Copie)`,
            start_date: originalSession.start_date,
            end_date: originalSession.end_date,
            status: 'Ouvert',
            session_type: originalSession.session_type,
            room_link: originalSession.room_link,
            room: originalSession.room,
            teacher_id: originalSession.teacher_id,
            session_id: originalSession.session_id // Relation vers session_groups
        };
        
        // Créer la nouvelle session
        const duplicatedSession = await createSessionFormation(duplicatedData);
        
        return duplicatedSession;
    } catch (error) {
        console.error(`Erreur lors de la duplication de la session ${sessionId}:`, error);
        throw error;
    }
};

export const duplicateMultipleSessionsFormation = async (sessionIds) => {
    try {
        const duplicatePromises = sessionIds.map(id => duplicateSessionFormation(id));
        const duplicatedSessions = await Promise.all(duplicatePromises);
        return duplicatedSessions;
    } catch (error) {
        console.error('Erreur lors de la duplication en masse des sessions:', error);
        throw error;
    }
};

export const getSessionParticipants = async (sessionId) => {
    try {
        const session = await pb.collection('sessions').getOne(sessionId);
        
        if (!session.session_id) {
            return [];
        }
        
        // Récupérer directement les membres du groupe de session
        const groupMembers = await pb.collection('session_group_members').getFullList({
            filter: `session_group_id = "${session.session_id}"`,
            expand: 'student_id'
        });
        
        return groupMembers.map(member => member.expand?.student_id).filter(Boolean);
    } catch (error) {
        console.error(`Erreur lors de la récupération des participants de la session ${sessionId}:`, error);
        throw error;
    }
};


export const generateQrCode = async (sessionId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/generate-qr-code`, {
      sessionId
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': pb.authStore.token
      }
    });

    const result = response.data;
    
    if (response.status !== 200) {
      throw new Error(result.error || 'Erreur lors de la génération du QR code');
    }

    return result;
  } catch (error) {
    console.error('Erreur lors de la génération du QR code:', error);
    throw error;
  }
};


export const closeSession = async (sessionId, signatureFile) => {
    if (!sessionId || !signatureFile) {
        throw new Error('ID d\'attendance et fichier de signature requis');
    }

    try {

        const authData = JSON.parse(localStorage.getItem('pocketbase_auth'));
        if (!authData || !authData.token) {
            throw new Error('Non authentifié');
        }
        
        const formData = new FormData();
        formData.append('signatureFile', signatureFile);
        
        const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/save-signature`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authData.token}`
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Erreur lors de la sauvegarde de la signature');
        }

        return await response.json();

    } catch (error) {
        console.error("Erreur lors de la sauvegarde de la signature:", error);
        throw new Error(`Erreur lors de l'enregistrement: ${error.message}`);
    }
};























// Fonction utilitaire pour récupérer les utilisateurs d'un groupe de session
export const getSessionGroupUsers = async (sessionId) => {
    try {
        const session = await pb.collection('sessions').getOne(sessionId);
        
        if (!session.session_id) {
            return [];
        }
        
        // Récupérer directement les membres du groupe de session
        const groupMembers = await pb.collection('session_group_members').getFullList({
            filter: `session_group_id = "${session.session_id}"`,
            expand: 'student_id,student_id.user_ref'
        });
        
        return groupMembers.map(member => {
            const student = member.expand?.student_id;
            const user = student?.expand?.user_ref;
            return {
                studentId: student?.id,
                userId: user?.id || student?.user_ref,
                student: student,
                user: user
            };
        }).filter(item => item.studentId);
    } catch (error) {
        console.error(`Erreur lors de la récupération des utilisateurs du groupe de session ${sessionId}:`, error);
        throw error;
    }
};